import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Does Derbyshire\'s RED overall rating guarantee my claim will succeed?',
    answer: 'No rating guarantees success. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired. But Derbyshire\'s RED overall and condition scorecards — with 20/100 on every DfT road-condition metric — are official evidence that the network is in the worst-rated band nationally, which can support a claim when combined with dated photos, prior reports and the road\'s survey cycle.',
  },
  {
    question: 'Why is spend rated AMBER when condition is RED?',
    answer: 'The DfT spend scorecard is separate from condition. Derbyshire projected £40m capital spend against a £41.174m DfT allocation in 2025/26 and estimated 60% preventative maintenance in 2024/25 — yet still earns AMBER on spend and best practice. Aggregate budgets do not prove the individual pothole was known and repaired within inspection intervals. Section 58 is about your specific defect, not headline spend.',
  },
  {
    question: 'Is Derbyshire County Council the same as Derby City Council?',
    answer: 'No. Derbyshire County Council maintains adopted highways across the county except within Derby City, which is a separate unitary authority. If your pothole was in Chesterfield, Matlock, Buxton or another Derbyshire town, your claim is against the county council. If it was inside Derby city limits, it goes to Derby City Council instead.',
  },
  {
    question: 'What if my pothole was on an unclassified or C-road?',
    answer: 'Unclassified roads make up 2,885km — 54% of Derbyshire\'s 5,346km carriageway network (derived). The council surveys approximately 20% of U-roads and 20% of C-roads annually on a five-year cycle, while B-roads are surveyed 100% each year. If your incident was on a C or U-road, there is a structural gap between network-level surveys and the day your damage occurred.',
  },
  {
    question: 'Can I reference the DfT ratings in my claim?',
    answer: 'Yes. The Department for Transport Local Road Maintenance Ratings 2025 to 2026 are published government data. Derbyshire\'s 20/100 scores on A, B/C and U-road condition — with RED overall and condition scorecards — can be cited alongside the council\'s own transparency report admissions.',
  },
  {
    question: 'The council says its pothole-fill table may be unreliable — can I still use the data?',
    answer: 'Use it cautiously. Derbyshire\'s transparency report states that "further analysis indicates inaccuracies in the table, therefore, we cannot confirm its reliability" for estimated potholes filled from 2020/21 to 2023/24. The council does confirm it usually spends £2 million to £3 million filling potholes each year. Prior reports, photos and inspection-cycle arguments are safer primary evidence than the disputed fill counts.',
  },
  {
    question: 'How do I report a pothole to Derbyshire County Council?',
    answer: 'Report road faults via the council\'s online system at derbyshire.gov.uk — you receive a unique reference number to track progress. Prior reports of the same defect strengthen a claim by demonstrating the council had notice before your incident. Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function DerbyshireCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Derbyshire Pothole Claims | 20/100 DfT Condition Scores | Fixtyer"
        description="Derbyshire County Council scores 20/100 on every DfT road-condition metric — RED overall and condition, AMBER on spend and best practice. U-road poor condition rose from 21% to 36% since 2020. See what that means for your claim."
        canonical="/councils/derbyshire"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Derbyshire', url: '/councils/derbyshire' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="Derbyshire County Council"
        councilSlug="derbyshire"
        rating="red"
      />
      <SpeakableSchema
        url="/councils/derbyshire"
        headline="Derbyshire Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Derbyshire</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="red" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            20/100 on Every DfT Road-Condition Metric — England&apos;s Lowest Band
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Derbyshire County Council — distinct from Derby City Council — maintains 5,346km of adopted highways
            across the county. The DfT rates it <strong className="text-rose-400">RED</strong> overall and on condition,
            with <strong className="text-amber-400">AMBER</strong> on spend and best practice. All three published
            condition metrics score <strong className="text-rose-400">20/100</strong> — the lowest band among{' '}
            154 rated English highway authorities. U-roads in poor condition rose from 21% to 36% between 2020 and 2024, the
            council admits &ldquo;under investment in preventative treatments&rdquo;, and most C and U-roads are condition-surveyed
            on a five-year cycle. Section 58 still turns on your specific defect.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">36%</div>
            <div className="text-white font-medium mt-2">U-roads in poor condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 21% in 2020 on 2,885km of unclassified roads — 54% of the network (derived) — while DfT
              condition scores sit at 20/100 on A, B/C and U-roads alike.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* DfT scorecard */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Department for Transport Ratings
          </h2>
          <p className="text-surface-600 mb-8">
            2025 to 2026 scorecards — RED on overall and condition; AMBER on spend and best practice
          </p>

          <div className="bg-surface-50 rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full">
              <caption className="sr-only">Derbyshire County Council DfT Road Maintenance Ratings 2025 to 2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-surface-700">Metric</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-surface-700">Score</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-surface-700">Rating</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-200">
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Overall rating</td>
                  <td className="px-6 py-4 text-center text-surface-600">—</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="red" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">A-road condition</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">20/100</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="red" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">B/C road condition</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">20/100</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="red" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">U-road condition</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">20/100</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="red" /></td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Condition scorecard</td>
                  <td className="px-6 py-4 text-center text-surface-600">—</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="red" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Spend scorecard</td>
                  <td className="px-6 py-4 text-center text-surface-600">—</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Best practice scorecard</td>
                  <td className="px-6 py-4 text-center text-surface-600">—</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
            <p className="text-amber-800 text-sm">
              <strong>Methodology caveat:</strong> Derbyshire measures condition using its Annual Engineers Inspection
              (AEI) treatment survey, not SCANNER laser surveys. The council states this &ldquo;has led to a more realistic
              view of our network and unfortunately this is at odds with most SCANNER based authorities&apos; road condition
              statistics.&rdquo; DfT scores and council percentages may not be directly comparable to laser-surveyed authorities.
            </p>
          </div>

          <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
            <p className="text-rose-800 text-sm">
              <strong>What RED condition means:</strong> A RED condition scorecard indicates the road network has
              significantly deteriorated. Derbyshire&apos;s 20/100 on all three road-class metrics places it in the
              lowest band among 154 rated English highway authorities — but spend and best practice are AMBER, not RED.
            </p>
          </div>

          <p className="mt-4 text-sm text-surface-500">
            Source:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>
          </p>
        </div>
      </section>

      {/* Network scale */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            5,346km of Roads — Mostly Unclassified
          </h2>
          <p className="text-surface-600 mb-8">
            Network scale from Derbyshire&apos;s 2025 transparency report — county roads only, excluding Derby City
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">5,346km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">2,885km</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-amber-700 mt-1">54% of the network (derived)</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">1,777km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">684km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">Derbyshire highway asset breakdown</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Asset</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Scale</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Footways</td>
                  <td className="px-6 py-4 text-center font-medium">5,113km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Public rights of way</td>
                  <td className="px-6 py-4 text-center font-medium">5,224km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Cycleways and greenways</td>
                  <td className="px-6 py-4 text-center font-medium">276km</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">Total highway asset value</td>
                  <td className="px-6 py-4 text-center font-medium">Over £8 billion</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="U-roads make up over 50% of Derbyshire's network and have historically been underfunded compared to more strategic hierarchies."
            source="Derbyshire County Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Condition deterioration */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Poor-Condition Share Rising Since 2020
          </h2>
          <p className="text-surface-600 mb-8">
            Red (poor condition) percentages from Derbyshire&apos;s AEI surveys — 2020 to 2024
          </p>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">A-roads (684km) — surveyed annually</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-surface-600">2020:</span>
                  <span className="font-medium">13% poor</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-surface-600">2024:</span>
                  <span className="font-bold text-rose-600">28% poor</span>
                </div>
                <div className="pt-3 border-t border-surface-200">
                  <span className="text-rose-600 font-bold">+115% rise in poor share (derived)</span>
                </div>
              </div>
            </div>

            <div className="bg-rose-50 rounded-2xl p-6 border border-rose-200">
              <h3 className="font-semibold text-rose-900 mb-4">B and C roads (1,777km)</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-surface-600">2020:</span>
                  <span className="font-medium">14% poor</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-surface-600">2024:</span>
                  <span className="font-bold text-rose-600">38% poor</span>
                </div>
                <div className="pt-3 border-t border-rose-200">
                  <span className="text-rose-600 font-bold">+171% rise in poor share (derived)</span>
                </div>
              </div>
              <p className="text-xs text-rose-700 mt-3">B-roads surveyed 100% annually; C-roads ~20% per year on a five-year cycle.</p>
            </div>

            <div className="bg-amber-50 rounded-2xl p-6 border border-amber-200">
              <h3 className="font-semibold text-amber-900 mb-4">U-roads (2,885km) — ~20% surveyed per year</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-surface-600">2020:</span>
                  <span className="font-medium">21% poor</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-surface-600">2024:</span>
                  <span className="font-bold text-rose-600">36% poor</span>
                </div>
                <div className="pt-3 border-t border-amber-200">
                  <span className="text-rose-600 font-bold">+71% rise in poor share (derived)</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
            <p className="text-amber-900 text-sm">
              <strong>Context:</strong> B and C roads in poor condition nearly tripled as a share of the network
              between 2020 and 2024. These are the school-run and commute routes — not motorways. U-road poor
              condition peaked at 43% in 2021 before settling at 36% in 2024, still well above the 2020 baseline.
            </p>
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            AMBER Spend Despite Rising Budgets
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            Capital spend is projected to match DfT allocation in 2025/26 — yet the spend scorecard stays AMBER
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Derbyshire highway maintenance spending 2020 to 2026</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr className="bg-amber-900/30">
                  <td className="px-4 py-3 font-medium">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">41,174</td>
                  <td className="px-4 py-3 text-center font-bold text-amber-400">40,000</td>
                  <td className="px-4 py-3 text-center">19,938</td>
                  <td className="px-4 py-3 text-center text-emerald-400">67%</td>
                  <td className="px-4 py-3 text-center">33%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">30,385</td>
                  <td className="px-4 py-3 text-center">28,148</td>
                  <td className="px-4 py-3 text-center">18,937</td>
                  <td className="px-4 py-3 text-center">60%</td>
                  <td className="px-4 py-3 text-center">40%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center">34,236</td>
                  <td className="px-4 py-3 text-center">31,629</td>
                  <td className="px-4 py-3 text-center">21,123</td>
                  <td className="px-4 py-3 text-center">60%</td>
                  <td className="px-4 py-3 text-center">40%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center">27,371</td>
                  <td className="px-4 py-3 text-center">32,501</td>
                  <td className="px-4 py-3 text-center">20,522</td>
                  <td className="px-4 py-3 text-center">61%</td>
                  <td className="px-4 py-3 text-center">39%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">Why spend is AMBER, not GREEN</h3>
              <p className="text-brand-200 text-sm">
                The DfT spend scorecard weighs capital spend against allocation and related metrics — not just
                the preventative/reactive split. Derbyshire projected £40m capital spend against £41.174m DfT
                allocation in 2025/26, close to parity, but still earns AMBER alongside an AMBER best-practice
                scorecard.
              </p>
            </div>
            <div className="bg-rose-500/20 rounded-xl p-6 border border-rose-500/30">
              <h3 className="font-semibold text-white mb-3">Why claims still happen</h3>
              <p className="text-brand-200 text-sm">
                Even at 60% preventative spend in 2024/25, the council confirms it usually spends £2 million to
                £3 million each year filling potholes — reactive work on a network where poor-condition shares are
                rising. Budget volume does not prove every defect was caught within inspection intervals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            £2–3 Million a Year on Pothole Filling
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volumes from the council&apos;s transparency report — with an official reliability caveat
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Data caveat:</strong> Derbyshire publishes estimated potholes filled from 2020/21 to 2023/24
              but states: &ldquo;Further analysis indicates inaccuracies in the table, therefore, we cannot confirm its
              reliability.&rdquo; The council is implementing a new asset management system. Treat fill counts as
              indicative, not definitive.
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Derbyshire estimated potholes filled 2020 to 2024</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Estimated potholes filled</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono">2020/21</td>
                  <td className="px-6 py-4 text-center">92,775</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono">2021/22</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">102,225</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono">2022/23</td>
                  <td className="px-6 py-4 text-center">85,726</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono">2023/24</td>
                  <td className="px-6 py-4 text-center">80,254</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="Depending on the number of potholes in a year we usually spend between £2 million and £3 million filling them."
            source="Derbyshire County Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Inspections and Section 58 */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspections, Surveys and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            How Derbyshire says it knows the condition of its network — and where gaps appear
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Condition survey frequency</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• <strong>A-roads:</strong> 100% surveyed annually</li>
                <li>• <strong>B-roads:</strong> 100% surveyed annually</li>
                <li>• <strong>C-roads:</strong> approximately 20% per year on a five-year cycle</li>
                <li>• <strong>U-roads:</strong> approximately 20% per year on a five-year cycle</li>
              </ul>
            </div>
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Resilient vs non-resilient network</h3>
              <p className="text-surface-600 text-sm">
                Derbyshire splits its network into &ldquo;resilient&rdquo; roads prioritised during adverse weather and a
                &ldquo;non-resilient&rdquo; remainder, using a hierarchy ranked 1 to 7. Most C and U-roads sit lower in
                that hierarchy — where survey gaps and underfunding admissions concentrate.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, Derbyshire must
              maintain public highways. To defend a claim under <strong className="text-white">Section 58</strong>,
              it must show a reasonable system for inspecting and repairing the specific defect — not just publish
              a RED DfT scorecard.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm mb-6">
              <li>• Was your road on the five-year C or U-road survey cycle — and had it been surveyed recently?</li>
              <li>• On C or U-roads, roughly 80% of the network is not condition-surveyed in any given year (derived from the 20% annual rate)</li>
              <li>• Were there prior reports (FixMyStreet, council portal) giving actual notice beyond network surveys?</li>
              <li>• Does photographic evidence show defect age beyond the inspection interval?</li>
            </ul>
            <p className="text-white font-medium">
              Combined with RED condition ratings, rising poor-condition shares and the council&apos;s own admissions
              of under-investment, these gaps give a Section 58 defence questions to answer — but your claim still
              needs specific evidence about your pothole.
            </p>
          </div>

          <QuoteCard
            quote="The overall decline in local road condition has not been helped by an under investment in preventative treatments and the impacts of extreme weather events."
            source="Derbyshire County Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Council admissions */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What Derbyshire Acknowledges
          </h2>
          <p className="text-surface-600 mb-8">
            Verbatim admissions from the 2025 transparency report
          </p>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On underfunding U-roads</h3>
              <QuoteCard
                quote="U-roads make up over 50% of Derbyshire's network and have historically been underfunded compared to more strategic hierarchies."
                source="Derbyshire County Council Local Highways Maintenance Transparency Report 2025"
              />
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On preventative maintenance failure</h3>
              <QuoteCard
                quote="The lack of preventative maintenance means that roads become less able to cope with extremes of temperature and severe weather, due to a loss of flexibility in the asphalt as it ages, causing it to pothole and fail."
                source="Derbyshire County Council Local Highways Maintenance Transparency Report 2025"
              />
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On national backlog context</h3>
              <QuoteCard
                quote="The poor condition of local roads has been a concern nationally for many years, with a figure of £16.81 billion quoted as a one-time catch-up figure nationally which would take 12 years to achieve."
                source="Derbyshire County Council Local Highways Maintenance Transparency Report 2025"
              />
            </div>
          </div>

          <p className="mt-8 text-sm text-surface-500">
            Source:{' '}
            <a
              href="https://www.derbyshire.gov.uk/transport-roads/highway-policy-funding/local-highways-maintenance-transparency-report/local-highways-maintenance-transparency-report.aspx"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Derbyshire County Council — Local Highways Maintenance Transparency Report
            </a>
          </p>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against England&apos;s Lowest-Rated County Network
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Derbyshire&apos;s RED condition data is among the strongest nationally — but spend and best practice are AMBER
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> AMBER spend scorecard — capital spend near DfT allocation in 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 60% preventative maintenance estimated in 2024/25, rising to 67% projected
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A and B-roads condition-surveyed 100% annually
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented resilient/non-resilient network hierarchy and asset management system upgrade
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence on A and B-roads with recent survey data. Do not overclaim on those corridors.
              </p>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <h3 className="font-semibold text-rose-900 mb-4">What works in yours</h3>
              <ul className="text-rose-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED overall and condition scorecards with 20/100 on every DfT road-class metric
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 2,885km of U-roads — 54% of network — on a five-year survey cycle
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-road poor condition at 36% in 2024, up from 21% in 2020
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> B/C poor condition share rose from 14% to 38% in four years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits under-investment in preventative treatments and historical U-road underfunding
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AEI methodology produces &ldquo;more realistic&rdquo; figures at odds with SCANNER-based authorities
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a county with England&apos;s lowest DfT condition band, your claim still lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole — proof of actual notice beyond network surveys</li>
              <li>• Photos showing defect size, depth and visible age (weathered edges, previous patching)</li>
              <li>• Road class — on a C or U-road, the five-year survey gap is your strongest structural argument</li>
              <li>• DfT 20/100 condition scores and council admissions cited where they support your road type</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: prior-report search, photo assessment, and citations from
              Derbyshire&apos;s own transparency data — without pretending every scorecard is RED.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a pothole to Derbyshire County Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. Do this before claiming — and tell us
            when you reported it so we can reference it in your pack.
          </p>
          <a
            href="https://www.derbyshire.gov.uk/transport-roads/roads-traffic/report-a-road-fault/report-a-road-fault.aspx"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a road fault — derbyshire.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Derbyshire?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            England&apos;s lowest DfT condition band demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No five-year C/U-road survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• Claims &ldquo;RED on every metric&rdquo; when spend is AMBER</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ Accurate DfT scorecard (RED condition, AMBER spend)</li>
                <li>✅ Five-year C/U-road survey cycle argued</li>
                <li>✅ 36% U-road poor condition and council admissions cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Derbyshire</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=derbyshire"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Derbyshire Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.derbyshire.gov.uk/transport-roads/highway-policy-funding/local-highways-maintenance-transparency-report/local-highways-maintenance-transparency-report.aspx"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Derbyshire County Council Local Highways Maintenance Transparency Report (2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
